/**
 * Hook React que encapsula MediaPipe Pose.
 *
 * Responsabilidades:
 *   - Pedir permiso de cámara al usuario
 *   - Cargar el modelo de MediaPipe Pose Lite
 *   - Correr inferencia por frame y emitir PoseResult
 *   - Manejar lifecycle (limpiar en unmount)
 *
 * Docs oficiales:
 *   https://developers.google.com/mediapipe/solutions/vision/pose_landmarker/web_js
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Keypoint, PoseResult } from "./types";

import { PoseLandmarker, FilesetResolver, type PoseLandmarkerResult } from "@mediapipe/tasks-vision";

export interface UseMediaPipePoseOptions {
  /** Elemento <video> sobre el cual correr inferencia. */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Si false, el hook no arranca (útil para renderizar el componente antes de pedir permiso). */
  enabled: boolean;
  /** Callback opcional: llamado en cada frame con el resultado. */
  onPose?: (pose: PoseResult) => void;
}

export interface UseMediaPipePoseReturn {
  pose: PoseResult | null;
  isLoading: boolean;
  error: string | null;
  /** Llamar para pedir permiso de cámara y arrancar. */
  start: () => Promise<void>;
  stop: () => void;
}

const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";

// Pinear el WASM a la misma versión que el package npm para evitar mismatches.
const WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";

export function useMediaPipePose(
  options: UseMediaPipePoseOptions,
): UseMediaPipePoseReturn {
  const { videoRef, enabled, onPose } = options;

  const [pose, setPose] = useState<PoseResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs que mutan sin re-render
  const landmarkerRef = useRef<PoseLandmarker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number>(0);
  const frameTimesRef = useRef<number[]>([]);
  // Tracker de mount status — evita setState/asignaciones después del unmount.
  // En dev StrictMode hace mount→unmount→mount; los awaits del primer mount
  // resuelven después de su cleanup y ensucian state/refs si no chequeamos.
  const mountedRef = useRef(true);

  // ==========================================================================
  // Init + shutdown
  // ==========================================================================

  const initMediaPipe = useCallback(async (): Promise<void> => {
    const vision = await FilesetResolver.forVisionTasks(WASM_URL);
    if (!mountedRef.current) return;
    const landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    if (!mountedRef.current) {
      landmarker.close();
      return;
    }
    landmarkerRef.current = landmarker;
  }, []);

  const start = useCallback(async () => {
    if (!videoRef.current) return;
    setIsLoading(true);
    setError(null);

    try {
      // 1) Pedir cámara
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
        audio: false,
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      if (!mountedRef.current) return;

      // 2) Inicializar MediaPipe
      await initMediaPipe();
      if (!mountedRef.current) return;

      // 3) Empezar loop de inferencia
      scheduleNextFrame();
      setIsLoading(false);
    } catch (e) {
      if (!mountedRef.current) return;
      // AbortError típico cuando StrictMode desmonta a media inicialización.
      if (e instanceof Error && e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : "Error desconocido");
      setIsLoading(false);
      stop();
    }
  }, [videoRef, initMediaPipe]);

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    landmarkerRef.current?.close();
    landmarkerRef.current = null;
  }, [videoRef]);

  // ==========================================================================
  // Loop de inferencia (corre una vez por frame disponible)
  // ==========================================================================

  const scheduleNextFrame = useCallback(() => {
    rafRef.current = requestAnimationFrame(processFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFrame = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;

    if (!video || !landmarker || video.readyState < 2) {
      scheduleNextFrame();
      return;
    }

    const now = performance.now();

    // MediaPipe requiere que el timestamp sea estrictamente creciente
    if (now <= lastTimestampRef.current) {
      scheduleNextFrame();
      return;
    }
    lastTimestampRef.current = now;

    const result: PoseLandmarkerResult = landmarker.detectForVideo(video, now);

    if (result.landmarks && result.landmarks.length > 0) {
      const keypoints: Keypoint[] = result.landmarks[0].map((lm) => ({
        x: lm.x,
        y: lm.y,
        z: lm.z,
        visibility: lm.visibility ?? 1,
      }));
      const fps = computeFps(now);
      const poseResult: PoseResult = {
        keypoints,
        timestamp: now,
        fps,
        // Dims intrínsecas para reglas aspect-ratio-independientes.
        // videoWidth/Height pueden ser 0 durante warm-up antes del
        // primer frame — los callers deben tolerar el 0.
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
      };
      setPose(poseResult);
      onPose?.(poseResult);
    }

    scheduleNextFrame();
  }, [onPose, scheduleNextFrame, videoRef]);

  // Calcula FPS rollado sobre los últimos 30 frames
  const computeFps = useCallback((now: number): number => {
    const times = frameTimesRef.current;
    times.push(now);
    while (times.length > 30) times.shift();
    if (times.length < 2) return 0;
    const span = times[times.length - 1] - times[0];
    return Math.round((times.length - 1) * 1000 / Math.max(span, 1));
  }, []);

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && !streamRef.current) {
      start();
    }
    return () => {
      mountedRef.current = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { pose, isLoading, error, start, stop };
}
